import { useCallback, useMemo, useState } from "react";
import { localStorageRecord } from "src/services/localStorage";
import { z } from "zod";

export function useLocalStorageRecord<T>(key: string, schema: z.ZodSchema<T>) {
  return useMemo(() => localStorageRecord(key, schema), [key, schema]);
}

type UseAutosavedStateProps<T> = {
  /** If not provided, the state will not be autosaved. */
  key?: string;
  schema: z.ZodSchema<T>;
  initialValue: T;
};
export function useAutosavedState<T>({
  key,
  schema,
  initialValue,
}: UseAutosavedStateProps<T>) {
  const record = useMemo(
    () => (key ? localStorageRecord(key, schema) : null),
    [key, schema]
  );
  const [value, setValue] = useState<T>(
    () => record?.getValue() ?? initialValue
  );
  const setValueWithAutosave = useCallback(
    (value: T) => {
      setValue(value);
      try {
        record?.setValue(value);
      } catch (error) {
        console.error("Error saving value to localStorage:", error);
      }
    },
    [record]
  );
  return [value, setValueWithAutosave] as const;
}
